import { SvgProsperfull } from '@assets/svgr/Prosperfull'
import { Button } from '@components/Elements/Button'
import { Icon } from '@components/Elements/Icon'
import { Pressable } from '@components/Elements/Pressable'
import { Text } from '@components/Elements/Text'
import { ScreenLayout } from '@components/Layouts'
import { getTrackingConfig, TrackingChartAssetTrackingTypeWithoutHeightEnum } from '@components/Tracking'
import { TrackingEventValue } from '@components/Tracking/styles'
import { api } from '@lib/api/api'
import { useDebounce } from '@lib/hooks'
import { useI18n } from '@lib/i18n'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useAppStore } from '@stores/app'
import { useAuthStore } from '@stores/auth'
import { useTasksStore } from '@stores/tasks'
import { useToastsStore } from '@stores/toasts'
import { useTrackingEventsStore } from '@stores/trackingEvents'
import { Colors } from '@vetahealth/fishing-gear/colors'
import { LastTrackingEventValue, PatientTask, TrackingTypeEnum } from '@vetahealth/tuna-can-api'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { ScrollView } from 'react-native'
import styled, { useTheme } from 'styled-components/native'
import { RouteName } from '../../../router/routeNames'

const Wrapper = styled.View`
    padding: 0 ${({ theme }) => (theme.width > 360 ? 20 : 10)}px 0px;
    gap: 20px;
`

function isTimeBetweenHours(startHour: number, endHour: number): boolean {
  return dayjs().isBetween(
    dayjs().set('hour', startHour).startOf('hour'),
    dayjs().set('hour', endHour).endOf('hour'),
    'hour',
    '[]',
  )
}

const TitleWrapper = styled.View``

function Title(): React.ReactNode {
  const t = useI18n()
  const [user] = useAuthStore((state) => [state.user])

  let greeting = t('dashboard.greeting.default')
  if (isTimeBetweenHours(4, 10)) greeting = t('dashboard.greeting.morning')
  if (isTimeBetweenHours(18, 24)) greeting = t('dashboard.greeting.evening')

  if (!user) return null

  return (
    <TitleWrapper>
      <Text size="xLarge" weight="bold">
        {greeting}
      </Text>
      <Text size="xLarge" weight="semiBold">
        {user?.firstName}
      </Text>
    </TitleWrapper>
  )
}

const TaskWrapper = styled.View`
  gap: 10px;
`
const NewTask = styled.View<{ hasSmallBorderRadius: boolean }>`
  padding: 15px 20px;
  background-color: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`
const NewTaskTag = styled.View`
  padding: 5px 10px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium}px;
`
const NewTaskBadge = styled.View`
  position: absolute;
  top: -2px;
  left: -2px;
  width: 15px;
  height: 15px;
  border-radius: 15px;
  background-color: ${Colors.ruby500};
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.background};
`
const NewTaskTitleWrapper = styled.View`
  flex: 1;
`
const AnsweredTasksWrapper = styled.View<{ hasSmallBorderRadius: boolean }>`
  padding: 15px 20px;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  gap: 5px;
`
const AnsweredTaskWrapper = styled.View<{ hasBorder: boolean }>`
  padding: 8px 0;
  border-top-width: ${({ hasBorder }) => (hasBorder ? 2 : 0)}px;
  border-color: ${({ theme }) => theme.colors.border};
`
const ButtonWrapper = styled.View`
  flex-direction: row;
  justify-content: flex-end;
`

function Tasks(): React.ReactNode {
  const t = useI18n()
  const theme = useTheme()
  const navigation = useNavigation()
  const [task, answeredTasks] = useTasksStore((state) => [state.task, state.answeredTasks])

  const handleNewTaskPress = useDebounce(() => {
    navigation.navigate(RouteName.ActiveTask, { taskRef: task?.ref })
  })

  const handleAnsweredTaskPress = useDebounce((responseId: PatientTask['responseId']) => {
    navigation.navigate(RouteName.AnsweredTask, { responseId })
  })

  const handleMoreAnsweredTasksPress = useDebounce(() => {
    navigation.navigate(RouteName.AnsweredTasks)
  })

  return (
    <TaskWrapper>
      {!!task && (
        <Pressable
          onPress={handleNewTaskPress}
          render={() => (
            <NewTask hasSmallBorderRadius={!!answeredTasks?.length}>
              <NewTaskBadge />
              <NewTaskTag>
                <Text disableLineHeight size="tiny" weight="bold" color={Colors.white}>
                  {t('dashboard.tasks.new')}
                </Text>
              </NewTaskTag>
              <NewTaskTitleWrapper>
                <Text
                  disableLineHeight
                  size="medium"
                  weight="semiBold"
                  numberOfLines={1}
                  color={theme.colors.primaryButtonText}
                >
                  {task.title}
                </Text>
              </NewTaskTitleWrapper>
            </NewTask>
          )}
        />
      )}

      {!!answeredTasks?.length && (
        <>
          <Text color={theme.colors.primaryButtonText} size="tiny" weight="semiBold">
            {t('dashboard.tasks.recently')}
          </Text>
          <AnsweredTasksWrapper hasSmallBorderRadius={!!task}>
            {answeredTasks.slice(0, 3).map((answeredTask, index) => {
              return answeredTask ? (
                <Pressable
                  key={answeredTask.responseId}
                  onPress={() => handleAnsweredTaskPress(answeredTask.responseId)}
                  render={() => (
                    <AnsweredTaskWrapper hasBorder={index !== 0}>
                      <Text size="button" weight="semiBold" numberOfLines={1}>
                        {answeredTask.title}
                      </Text>
                      <Text size="tiny" weight="medium" color={theme.colors.primaryButtonText}>
                        {dayjs(answeredTask.timestamp).fromNow()}
                      </Text>
                    </AnsweredTaskWrapper>
                  )}
                />
              ) : null
            })}
            <ButtonWrapper>
              <Button variant="secondary" label={t('dashboard.button.all')} onPress={handleMoreAnsweredTasksPress} />
            </ButtonWrapper>
          </AnsweredTasksWrapper>
        </>
      )}
    </TaskWrapper>
  )
}

const LastTrackingEventContainer = styled.View`
  gap: 10px;
`
const LastTrackingEventWrapper = styled.View<{ color: Colors }>`
  min-width: 120px;
  padding: 15px 20px;
  background-color: ${({ theme }) => theme.colors.trackingEventBackground};
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  gap: 2px;
`
const LastTrackingEventTypeWrapper = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
`
const LastTrackingEventValueWrapper = styled.View`
`

function TrackingEvents(): React.ReactNode {
  const t = useI18n()
  const theme = useTheme()
  const navigation = useNavigation()
  const [lastTrackingEvents] = useTrackingEventsStore((state) => [state.lastTrackingEvents])

  const handleTrackingEventPress = useDebounce((trackingType: TrackingChartAssetTrackingTypeWithoutHeightEnum) => {
    navigation.navigate(RouteName.Tracking, {
      screen: RouteName.TrackingByType,
      params: { trackingType },
    })
  })

  const handleAllTrackingEventsPress = useDebounce(() => {
    navigation.navigate(RouteName.Tracking, { screen: RouteName.TrackingList })
  })

  if (!lastTrackingEvents || Object.values(lastTrackingEvents).every((event) => event === null)) return null

  const sortedLastTrackingEvents: LastTrackingEventValue[] = Object.values(lastTrackingEvents)
    .filter((item) => !!item && item.type !== TrackingTypeEnum.Height)
    .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
    .slice(0, 5)

  return (
    <LastTrackingEventContainer>
      <Text color={theme.colors.primaryButtonText} size="tiny" weight="semiBold">
        {t('dashboard.tracking.recently')}
      </Text>
      <ScrollView horizontal contentContainerStyle={{ gap: 10, alignItems: 'center' }}>
        {sortedLastTrackingEvents.map((lastTrackingEvent) => {
          const trackingType = lastTrackingEvent.type as TrackingChartAssetTrackingTypeWithoutHeightEnum
          const trackingConfig = getTrackingConfig(trackingType)

          return (
            <Pressable
              key={lastTrackingEvent.type}
              onPress={() => handleTrackingEventPress(trackingType)}
              render={() => (
                <LastTrackingEventWrapper color={trackingConfig.color}>
                  <LastTrackingEventTypeWrapper>
                    <Icon icon={trackingConfig.icon} color={trackingConfig.color} size={26} />
                    <Text color={trackingConfig.color} numberOfLines={1} size="button" weight="semiBold">
                      {trackingConfig.title}
                    </Text>
                  </LastTrackingEventTypeWrapper>
                  <LastTrackingEventValueWrapper>
                    <TrackingEventValue
                      displayValue={trackingConfig.getDisplayValue(
                        [lastTrackingEvent.value, lastTrackingEvent.additionalValue ?? 0],
                        lastTrackingEvent.unit,
                      )}
                    />
                  </LastTrackingEventValueWrapper>
                  <Text size="tiny" weight="medium" color={theme.colors.primaryButtonText}>
                    {dayjs(lastTrackingEvent.timestamp).fromNow()}
                  </Text>
                </LastTrackingEventWrapper>
              )}
            />
          )
        })}
        <Button variant="secondary" label={t('dashboard.button.all')} onPress={handleAllTrackingEventsPress} />
      </ScrollView>
    </LastTrackingEventContainer>
  )
}

export function Dashboard(): React.ReactNode {
  const isFocused = useIsFocused()
  const [isDevMode] = useAppStore((state) => [state.isDevMode])
  const [addToast] = useToastsStore((state) => [state.addToast])
  const [getTask, getAnsweredTasks, reset] = useTasksStore((state) => [
    state.getTask,
    state.getAnsweredTasks,
    state.reset,
  ])
  const [getLastTrackingEvents] = useTrackingEventsStore((state) => [state.getLastTrackingEvents])

  function getDashboardData() {
    getTask()
    getAnsweredTasks()
    getLastTrackingEvents()
  }

  async function handleLogoPress() {
    if (isDevMode) {
      addToast({
        title: 'Task reset',
        message: 'Do you want to reset todays answered tasks?',
        primaryAction: { label: 'Reset', onPress: handleTaskReset },
        duration: Infinity,
      })
    }
  }

  async function handleTaskReset() {
    if (isDevMode) {
      await api.devDeleteTodaysTasks()
      addToast({ message: 'Tasks successfully reset!', duration: 3000 })
      reset()
      getDashboardData()
    }
  }

  useEffect(() => {
    if (isFocused) {
      getDashboardData()
    }
  }, [isFocused])

  return (
    <ScreenLayout
      title={<Pressable onLongPress={handleLogoPress} render={() => <SvgProsperfull width={150} height={70} />} />}
    >
      <Wrapper>
        <Title />
        <Tasks />
        <TrackingEvents />
      </Wrapper>
    </ScreenLayout>
  )
}
